import { css, styled, type Theme } from '@mui/material';

import _InlineMessageIcon from './InlineMessageIcon';
import { inlineMessageClasses } from './theme';

import type { InlineMessageSize, InlineMessageVariant } from './constants';
import type { InlineMessageOwnerState, InlineMessageProps } from './types';

const InlineMessage = (props: InlineMessageProps) => {
  const {
    disabled = false,
    icon = true, // theme defaultProps에서 이동
    message,
    size = 'xs', // theme defaultProps에서 이동
    variant = 'error', // theme defaultProps에서 이동
    ...others
  } = props;

  const ownerState: InlineMessageOwnerState = {
    disabled,
    icon,
    message,
    size,
    variant,
    ...others,
  };

  return (
    <InlineMessageRoot ownerState={ownerState} {...others}>
      {icon && <InlineMessageIcon variant={variant} ownerState={ownerState} />}
      <InlineMessageMessage ownerState={ownerState}>{message}</InlineMessageMessage>
    </InlineMessageRoot>
  );
};

export default InlineMessage;

// Styled Components
const InlineMessageRoot = styled('div', {
  target: inlineMessageClasses.root,
})<{ ownerState: InlineMessageOwnerState }>(({ ownerState }) => {
  const { size } = ownerState;

  return css`
    display: flex;
    align-items: flex-start;

    ${getSizeRootStyles(size)}
  `;
});

const InlineMessageIcon = styled(_InlineMessageIcon, {
  target: inlineMessageClasses.icon,
})<{ ownerState: InlineMessageOwnerState }>(({ ownerState, theme }) => {
  const { size, variant, disabled } = ownerState;

  return css`
    ${getSizeIconStyles(size)}
    ${getVariantIconStyles(variant, disabled, theme)}
  `;
});

const InlineMessageMessage = styled('span', {
  target: inlineMessageClasses.message,
})<{ ownerState: InlineMessageOwnerState }>(({ ownerState, theme }) => {
  const { size, variant, icon, disabled } = ownerState;

  return css`
    white-space: pre-wrap;
    margin-top: 2px;
    ${getSizeMessageStyles(size, theme)}
    ${getVariantMessageStyles(variant, icon, disabled, theme)}
  `;
});

// Style helper functions
const getSizeRootStyles = (size?: InlineMessageSize) => {
  const sizeStylesMap = {
    xs: css`
      min-height: 22px;
      gap: 4px;
    `,
    sm: css`
      min-height: 24px;
      gap: 4px;
    `,
    md: css`
      min-height: 25px;
      gap: 6px;
    `,
    lg: css`
      min-height: 28px;
      gap: 6px;
    `,
  };
  return size ? sizeStylesMap[size] : css``;
};

const getSizeIconStyles = (size?: InlineMessageSize) => {
  const sizeStylesMap = {
    xs: css`
      font-size: 12px;
      margin-top: 5px;
      margin-left: 2px;
    `,
    sm: css`
      font-size: 16px;
      margin-top: 4px;
      margin-left: 2px;
    `,
    md: css`
      font-size: 20px;
      margin-top: 2px;
      margin-left: 2px;
    `,
    lg: css`
      font-size: 24px;
      margin-top: 2px;
      margin-left: 2px;
    `,
  };
  return size ? sizeStylesMap[size] : css``;
};

const getVariantIconStyles = (
  variant?: InlineMessageVariant,
  disabled?: boolean,
  theme?: Theme,
) => {
  if (!theme) return css``;

  if (disabled) {
    return css`
      color: ${theme.color['icon/disabled']};
    `;
  }

  const variantStylesMap = {
    error: css`
      color: ${theme.color['icon/danger/default']};
    `,
    warning: css`
      color: ${theme.color['icon/warning/default']};
    `,
    information: css`
      color: ${theme.color['icon/informative/default']};
    `,
    success: css`
      color: ${theme.color['icon/success/default']};
    `,
    help: css`
      color: ${theme.color['icon/weaker/default']};
    `,
  };
  return variant ? variantStylesMap[variant] : css``;
};

const getSizeMessageStyles = (size?: InlineMessageSize, theme?: Theme) => {
  if (!theme) return css``;

  const sizeStylesMap = {
    xs: css`
      ${theme.typography['label/regular/xs']};
    `,
    sm: css`
      ${theme.typography['label/regular/sm']};
    `,
    md: css`
      ${theme.typography['label/regular/md']};
    `,
    lg: css`
      ${theme.typography['label/regular/lg']};
    `,
  };
  return size ? sizeStylesMap[size] : css``;
};

const getVariantMessageStyles = (
  variant?: InlineMessageVariant,
  icon?: boolean | null,
  disabled?: boolean,
  theme?: Theme,
) => {
  if (!theme) return css``;

  if (disabled) {
    return css`
      color: ${theme.color['text/disabled']};
    `;
  }

  // icon이 있고 variant가 help인 경우
  if (icon && variant === 'help') {
    return css`
      color: ${theme.color['text/weaker/default']};
    `;
  }

  // icon이 있는 경우 (help 제외)
  if (icon) {
    return css`
      color: ${theme.color['text/weak/default']};
    `;
  }

  // icon이 없는 경우, variant별 색상 적용
  const variantStylesMap = {
    error: css`
      color: ${theme.color['text/danger/default']};
    `,
    warning: css`
      color: ${theme.color['text/warning/default']};
    `,
    information: css`
      color: ${theme.color['text/informative/default']};
    `,
    success: css`
      color: ${theme.color['text/success/default']};
    `,
    help: css`
      color: ${theme.color['text/weaker/default']};
    `,
  };
  return variant ? variantStylesMap[variant] : css``;
};
